import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { 
  Lightning, House, MagnifyingGlass, FileText, Bookmark, 
  User, Bell, SignOut, ChartLineUp, Crown, CaretDown, List, X, CalendarBlank, Storefront, Buildings, FolderOpen, Star, HandshakeIcon, Compass, UsersThree, Sparkle
} from "@phosphor-icons/react";
import axios from "axios";
import { Toaster, toast } from "sonner";

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const prevUnreadRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      const newNotifs = res.data.notifications || [];
      const newUnread = res.data.unread_count || 0;
      setNotifications(newNotifs);
      setUnreadCount(newUnread);

      // Toast alert when new notifications arrive
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const latest = newNotifs.find(n => !n.read);
        if (latest) {
          toast(latest.title, {
            description: latest.message,
            action: latest.type === "grant" ? {
              label: "View",
              onClick: () => navigate("/grants")
            } : undefined
          });
        }
      }
      prevUnreadRef.current = newUnread;
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [navigate]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markNotificationRead = async (notif) => {
    try {
      await axios.put(`${API}/notifications/${notif.notification_id}/read`, {});
      setNotifications(prev => prev.map(n => 
        n.notification_id === notif.notification_id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Navigate to grants if it's a grant notification
      if (notif.type === "grant") {
        setShowNotifications(false);
        navigate("/grants");
      }
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {});
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error("Error marking notifications read:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", icon: House, label: "Dashboard" },
    { path: "/grants", icon: MagnifyingGlass, label: "Grants" },
    { path: "/applications", icon: FileText, label: "Apps" },
    { path: "/bookmarks", icon: Bookmark, label: "Saved" },
    { path: "/calendar", icon: CalendarBlank, label: "Calendar" },
    { path: "/documents", icon: FolderOpen, label: "Docs" },
    ...(user?.subscription_tier === "pro" || user?.role === "admin" ? [{ path: "/post-grants", icon: ChartLineUp, label: "Post-Grant" }] : []),
    ...(user?.subscription_tier === "pro" || user?.role === "admin" ? [{ path: "/vendors", icon: Storefront, label: "Vendors" }] : []),
    ...(user?.subscription_tier === "pro" || user?.role === "admin" ? [{ path: "/corporate-scout", icon: Buildings, label: "Scout" }] : []),
    ...(user?.subscription_tier === "pro" || user?.role === "admin" ? [{ path: "/impact", icon: Star, label: "Impact" }] : []),
    ...(user?.subscription_tier === "pro" || user?.role === "admin" || ["trialing", "active"].includes(user?.trial_status) ? [{ path: "/formation", icon: Sparkle, label: "Formation" }] : []),
    { path: "/collaborate", icon: HandshakeIcon, label: "Find Partners" },
    { path: "/coalitions", icon: UsersThree, label: "Coalitions" },
    { path: "/sponsorships", icon: HandshakeIcon, label: "Sponsors" },
    { path: "/pathways", icon: Compass, label: "Pathways" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Toaster position="bottom-right" theme="dark" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-800/50">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 gap-2">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
            <Lightning weight="fill" className="w-7 h-7 text-emerald-500" />
            <span className="text-lg font-bold tracking-tight hidden sm:block">FlashGrants</span>
          </Link>

          {/* Desktop Nav - icon-only at lg, icon+label at xl */}
          <nav className="hidden lg:flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-hide">
            {navItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path}
                title={item.label}
                className={`px-2.5 py-2 rounded-sm text-sm flex items-center gap-1.5 transition-colors ${
                  location.pathname === item.path 
                    ? 'bg-emerald-500/10 text-emerald-400' 
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="hidden 2xl:inline">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Subscription Badge */}
            {user?.subscription_tier !== "free" && (
              <span className="hidden 2xl:inline-flex badge-emerald rounded-sm px-2 py-0.5 text-xs uppercase">
                {user?.subscription_tier}
              </span>
            )}

            {/* Admin Link */}
            {user?.role === "admin" && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/admin")}
                className="text-yellow-400 hover:text-yellow-300"
                data-testid="admin-link"
                title="Admin Dashboard"
              >
                <Crown className="w-4 h-4" />
              </Button>
            )}

            {/* Notifications */}
            <div className="relative">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative"
                data-testid="notifications-btn"
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="notification-dot" />
                )}
              </Button>

              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-zinc-950 border border-zinc-800 rounded-sm shadow-xl z-50">
                  <div className="flex items-center justify-between p-3 border-b border-zinc-800">
                    <span className="font-semibold text-sm">Notifications {unreadCount > 0 && <span className="text-emerald-400 text-xs ml-1">({unreadCount} new)</span>}</span>
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllRead}
                        className="text-xs text-emerald-400 hover:underline"
                        data-testid="mark-all-read-btn"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-center text-zinc-500 text-sm">No notifications yet</p>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <button 
                          key={notif.notification_id}
                          onClick={() => !notif.read && markNotificationRead(notif)}
                          className={`w-full text-left p-3 border-b border-zinc-800 last:border-0 transition-colors hover:bg-zinc-900 ${!notif.read ? 'bg-emerald-500/5 border-l-2 border-l-emerald-500' : ''}`}
                          data-testid={`notification-${notif.notification_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${!notif.read ? 'text-white' : 'text-zinc-400'}`}>{notif.title}</p>
                            {!notif.read && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />}
                          </div>
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{notif.message}</p>
                          {notif.created_at && (
                            <p className="text-xs text-zinc-600 mt-1">{new Date(notif.created_at).toLocaleDateString()}</p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative hidden sm:block">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-1.5"
                data-testid="user-menu-btn"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <CaretDown className="w-3 h-3" />
              </Button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-zinc-950 border border-zinc-800 rounded-sm shadow-xl z-50">
                  <div className="p-3 border-b border-zinc-800">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-zinc-400">{user?.email}</p>
                  </div>
                  <div className="p-2">
                    <Link 
                      to="/profile"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                    {user?.subscription_tier !== "pro" && (
                      <Link 
                        to="/pricing"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-400 hover:bg-emerald-500/10 rounded-sm"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Lightning className="w-4 h-4" />
                        Upgrade
                      </Link>
                    )}
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-sm"
                      data-testid="logout-dropdown-btn"
                    >
                      <SignOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Always-visible Sign Out button */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20"
              data-testid="signout-btn"
              title="Sign Out"
            >
              <SignOut className="w-5 h-5" />
            </Button>

            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-zinc-800 bg-zinc-950 p-4">
            {navItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-sm text-sm ${
                  location.pathname === item.path 
                    ? 'bg-emerald-500/10 text-emerald-400' 
                    : 'text-zinc-400'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
            <div className="border-t border-zinc-800 mt-2 pt-2">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-sm text-red-400 hover:bg-red-500/10"
                data-testid="mobile-logout-btn"
              >
                <SignOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          {children}
        </div>
      </main>

      {/* Click outside handlers */}
      {(showNotifications || showUserMenu) && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => { setShowNotifications(false); setShowUserMenu(false); }}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
